/**
 * "First map view" signal: GA and Sentry wait for it so their scripts don't
 * compete with the first map for the network and the main thread.
 *
 * whenFirstMapView() resolves exactly once, on whichever comes first:
 * - the main map's first `tilesloaded` (GoogleMapsEngine calls markFirstMapView);
 * - a view that shows no map: the legal/privacy/usage pages (at once), or the
 *   sign-in form (Login calls markFirstMapView when it shows it);
 * - a fallback, FALLBACK_MS after the window `load` event, restarted when the
 *   map is created (noteMapCreated), so a map that never shows tiles, or a
 *   page without one such as admin, still gets analytics and error reporting.
 */

// Pages that never show a map. index.js also skips the Google Maps preload here.
const MAP_LESS_PATH_RE = /^\/(legal|privacy|usage)(\/|$)/;

// On a throttled phone `load` fires at ~7-12 s, the map is created at
// ~14-23 s and the first tiles follow ~5 s later; the fallback must not beat
// them, it is only for views where no tiles ever come.
export const FALLBACK_MS = 20000;

let resolveSignal;
let marked = false;
let armed = false;
let fallbackTimer = null;
const signal = new Promise((resolve) => {
  resolveSignal = resolve;
});

export function isMapLessPath(pathname) {
  return MAP_LESS_PATH_RE.test(pathname || "");
}

/** Mark the first map view (idempotent). */
export function markFirstMapView() {
  if (marked) return;
  marked = true;
  clearTimeout(fallbackTimer);
  resolveSignal();
}

function startFallback() {
  if (marked) return;
  clearTimeout(fallbackTimer);
  fallbackTimer = setTimeout(markFirstMapView, FALLBACK_MS);
}

/** The map exists and its tiles are on the way: restart the fallback. */
export function noteMapCreated() {
  if (armed) startFallback();
}

function arm() {
  if (armed) return;
  armed = true;
  if (
    typeof window === "undefined" ||
    isMapLessPath(window.location.pathname)
  ) {
    markFirstMapView();
    return;
  }
  if (document.readyState === "complete") {
    startFallback();
  } else {
    window.addEventListener("load", startFallback, { once: true });
  }
}

/** Promise that resolves once the first map view is up (see above). */
export function whenFirstMapView() {
  arm();
  return signal;
}
